import type { ArtifactSourceRef, ArtifactThemeMode, InitMessage } from "@entrilia/shared/artifactRuntime/protocol";
import { getUserLocaleCode } from "@entrilia/shared/utilities/userLocaleConfig";
import type {
  SmartViewArtifactSource,
  SmartViewArtifactVersion,
} from "../../../../../api/types/smartViewArtifactsTypes";

// A deeper view keeps the clicked values of the views above it, so a linked source also takes its ancestors' link keys.
const getLinkFilterKeysWithAncestors = (
  source: SmartViewArtifactSource,
  sources: SmartViewArtifactSource[]
): string[] => {
  const keys = new Set<string>();
  const visited = new Set<string>();
  let current: SmartViewArtifactSource | undefined = source;
  while (current?.link && !visited.has(current.key)) {
    visited.add(current.key);
    current.link.filterKeys.forEach((key) => keys.add(key));
    const parentKey: string = current.link.parentSourceKey;
    current = sources.find((candidate) => candidate.key === parentKey);
  }
  return [...keys];
};

const toSourceRef = (source: SmartViewArtifactSource, sources: SmartViewArtifactSource[]): ArtifactSourceRef => ({
  key: source.key,
  boundFilterKeys: [...new Set(source.filterBindings.map((binding) => binding.filterKey))],
  linkFilterKeys: getLinkFilterKeysWithAncestors(source, sources),
});

interface InitFlags {
  build: boolean;
  drillMode: boolean;
}

export const createInitMessage = (
  version: SmartViewArtifactVersion,
  filterValues: Record<string, string[]>,
  filterLabels: Record<string, Record<string, string>>,
  mode: ArtifactThemeMode,
  { build, drillMode }: InitFlags,
  logo: string | undefined
): InitMessage => ({
  type: "init",
  version: version.version,
  files: version.files,
  sources: version.sources.map((source) => toSourceRef(source, version.sources)),
  filters: version.filters.map(({ key, label, kind, singleSelect }) => ({ key, label, kind, singleSelect })),
  filterValues,
  filterLabels,
  views: version.views.map(({ key, title, linkFilterKeys }) => ({ key, title, linkFilterKeys })),
  style: version.style,
  logo,
  mode,
  localeCode: getUserLocaleCode(),
  build,
  drillMode,
});
